import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  useHasRole,
  useIsExternalClient,
  useIsInternalUser,
  useTier,
} from '@/lib/auth/AuthProvider';
import type { ResponseFormat } from '@/types/api';

import { ChatInputBar } from './components/ChatInputBar';
import { ContextPanel } from './components/ContextPanel';
import { MessageBubble } from './components/MessageBubble';
import { ThreadList } from './components/ThreadList';
import { useSubmitQuery, useThreadTurns } from './hooks';
import { useChatStore } from './store/chatStore';

/**
 * Spec S04 — three-zone layout: thread sidebar | messages | context (v2).
 * Phase 6 ships the left + centre panels; right context panel is a placeholder
 * (home dashboard widget lands in Phase 6.7).
 */
export function ChatPage() {
  const { threadId } = useParams();
  const [searchParams] = useSearchParams();
  // `?q=` lets deep-links (e.g. the map entity popup) seed the input
  // without auto-submitting — the analyst still confirms.
  const initialQuery = searchParams.get('q') ?? undefined;
  const isExternalClient = useIsExternalClient();
  const isInternalUser = useIsInternalUser();
  const isAnalyst = useHasRole('data_analyst_standard', 'data_analyst_domain_expert');
  const tier = useTier();

  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const responseFormat = useChatStore((s) => s.responseFormat);
  const setResponseFormat = useChatStore((s) => s.setResponseFormat);
  const setActiveThread = useChatStore((s) => s.setActiveThread);
  const hydrateFromHistory = useChatStore((s) => s.hydrateFromHistory);
  const quotaRemaining = useChatStore((s) => s.quotaRemaining);

  const { data: threadDetail } = useThreadTurns(threadId ?? null);
  const submit = useSubmitQuery();

  // When the URL changes, swap active thread and hydrate.
  useEffect(() => {
    setActiveThread(threadId ?? null);
  }, [threadId, setActiveThread]);

  useEffect(() => {
    if (threadDetail) hydrateFromHistory(threadDetail.turns);
  }, [threadDetail, hydrateFromHistory]);

  // Auto-scroll the messages pane to the latest turn on new content or
  // while a streaming response is filling in. Uses scrollTop on the
  // container rather than scrollIntoView so the page itself doesn't
  // get yanked around when the chat is in a tall layout.
  const messagesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, isStreaming]);

  const disabledFormats: ResponseFormat[] = computeDisabledFormats({
    isExternalClient,
    tier,
  });
  const quotaExhausted = quotaRemaining === 0;

  return (
    <div className="grid h-[calc(100vh-3.5rem)] min-h-0 grid-cols-[240px_1fr_320px]">
      <ThreadList />

      <section className="flex h-full min-h-0 flex-col">
        {/* `min-h-0` lets the flex child respect the parent's height
            instead of growing to fit content, which is what makes
            overflow-y-auto kick in and keeps the input bar visible. */}
        <div
          ref={messagesRef}
          className="min-h-0 flex-1 overflow-y-auto px-6 py-6"
        >
          {messages.length === 0 && (
            <div className="flex h-full items-center justify-center text-center">
              <div>
                <p className="text-h2 text-brand-900">Ask about the energy transition</p>
                <p className="mt-1 text-body text-slate-600">
                  Try: "Which 200 MW hydrogen projects in Germany are at FID?"
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {messages.map((m) => (
              <MessageBubble
                key={m.turnId}
                message={m}
                isInternal={isInternalUser}
                isAnalyst={isAnalyst}
                onClarificationChip={(text) =>
                  submit.mutate({ question: text, thread_id: threadId ?? undefined })
                }
              />
            ))}
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white">
          <ChatInputBar
            isSubmitting={isStreaming}
            quotaExhausted={quotaExhausted}
            responseFormat={responseFormat}
            onResponseFormatChange={setResponseFormat}
            disabledFormats={disabledFormats}
            initialValue={initialQuery}
            onSubmit={(question) =>
              submit.mutate({
                question,
                thread_id: threadId ?? undefined,
                response_format: responseFormat,
              })
            }
          />
        </div>
      </section>

      <aside className="hidden h-full min-h-0 overflow-y-auto border-l border-slate-200 bg-white px-4 py-4 xl:block">
        <ContextPanel quotaRemaining={quotaRemaining} />
      </aside>
    </div>
  );
}

// ─── Format restrictions per tier (Spec S04 permission matrix) ──

function computeDisabledFormats({
  isExternalClient,
  tier,
}: {
  isExternalClient: boolean;
  tier: string | undefined;
}): ResponseFormat[] {
  if (!isExternalClient) return [];
  if (tier === 'free') return ['map', 'graph', 'report'];
  if (tier === 'analyst') return ['report'];
  return [];
}
